import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import React, { ReactElement, FunctionComponent } from 'react'
import { GridProps } from '@sport1/react-elements/core/types'
import { PlatformHelper } from '@sport1/react-elements'
import { Variants } from '../../DesignSystem/types'
import LinkProps from '../../Link/LinkProps'
import { spacings } from '../../DesignSystem/spacings'
import colorPattern from '../../DesignSystem/colors'
import { TestID } from '../../utils/PropTypesDefinitions'
import { useTabVariantStyles } from './useTabVariantStyles'
import Font from '@/Fonts/Font'
import Image from '@/BaseComponents/Image'

export type TabProps = {
    /** Text of the tab */
    title?: string
    /** Color variant */
    tabVariant?: Extract<Variants, 'primary' | 'secondary' | 'tertiary'>
    /** Shows if the Tab is selected */
    isActive?: boolean
    /** Determines if the tab occupies the whole space available */
    expand?: boolean
    /** Redirects user to the value assigned */
    href?: string
    /**
     * The callback when the user presses on the forward icon
     *
     * @TJS-type function
     */
    onPress?: () => void
    /**
     * Object that allows to pass additional properties to the Grid
     * component wrapping the inner text of the Tab.
     */
    innerTextWrapper?: GridProps
    /**
     * Image url to display on the left side of the Tab
     * This property is used for so called 'Static Tab' without the ability to be clicked
     */
    imageUrl?: string
    /** Info about imageUrl for SEO */
    alt?: string
} & LinkProps &
    TestID

const Tab: FunctionComponent<TabProps> = ({
    tabVariant = 'primary',
    title,
    isActive = false,
    expand,
    href,
    onPress,
    testID,
    webRel,
    webTarget,
    innerTextWrapper,
    imageUrl,
    alt,
}): ReactElement | null => {
    const { backgroundColor, fontColor, selectionColor } = useTabVariantStyles(tabVariant)

    if (!title) return null

    return (
        <Touchable
            onPress={onPress}
            href={href}
            webRel={webRel}
            webTarget={webTarget}
            testID={testID}
        >
            <Grid noExpand horizontal={!expand}>
                <Grid
                    height={44}
                    paddingRight={spacings['spacing-6']}
                    paddingLeft={spacings['spacing-6']}
                    centerVertical
                    noExpand
                    horizontal={!expand}
                    backgroundColor={backgroundColor}
                    {...innerTextWrapper}
                    borderBottom="2px"
                    borderColor={isActive ? selectionColor : 'transparent'}
                    testID="content-wrapper"
                >
                    {imageUrl && (
                        <Grid
                            centerHorizontal
                            centerVertical
                            backgroundColor={colorPattern.moon}
                            width={20}
                            height={20}
                            noExpand
                            marginRight={spacings['spacing-6']}
                        >
                            <Image
                                imageUrl={imageUrl}
                                alt={alt}
                                width={16}
                                aspectRatio="ORIGINAL"
                                resizeMode="contain"
                                size="S"
                            />
                        </Grid>
                    )}

                    <Font
                        fontVariant="Body-S"
                        fontFamilyVariant="Sport1-CondensedBlackItalic"
                        color={fontColor}
                        textAlign="center"
                        numberOfLines={1}
                        disableLineHeight={
                            PlatformHelper.onAndroid &&
                            PlatformHelper?.platformVersion &&
                            PlatformHelper.platformVersion >= 35
                        }
                    >
                        {title}
                    </Font>
                </Grid>
            </Grid>
        </Touchable>
    )
}

export default Tab
